import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, UploadSimple, PencilSimple, Trash, ArrowLeft, DownloadSimple, MapPin } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useRealtime } from "../../lib/realtime";
import { TemplateEditor } from "./TemplateEditor";
import { ImportWizard, downloadSample } from "./ImportWizard";
import { LocalLinesEditor } from "./LocalLinesEditor";
import { useHierarchy, distributionLabel } from "./DistributionPicker";
import { categoryLabel, shiftLabel, btnPrimary, btnGhost } from "./shared";

export function TemplatesView({ config, departments, venues, venueId, onBack }) {
  const { user } = useAuth();
  const isTop = ["admin", "gm"].includes(user.role) || user.is_master_operator;
  const [templates, setTemplates] = useState([]);
  const [editor, setEditor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [localFor, setLocalFor] = useState(null);
  const hier = useHierarchy();
  const canPush = config ? config.can_manage_templates : isTop;
  const canLocal = !!config?.can_manage_local_lines;

  const load = useCallback(() => api.get("/checklists/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useRealtime((msg) => { if (msg.type === "checklist.template") load(); });

  const names = (ids, list, empty) => (ids?.length ? ids.map((id) => list.find((x) => x.id === id)?.name).filter(Boolean).join(", ") : empty);

  const toggleActive = async (t) => {
    try {
      await api.put(`/checklists/templates/${t.id}`, { ...t, active: !t.active });
      toast.success(t.active ? "Template paused" : "Template activated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Past checklist records are kept.`)) return;
    try { await api.delete(`/checklists/templates/${t.id}`); toast.success("Template deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="templates-view">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <button data-testid="templates-back-button" onClick={onBack} className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2"><ArrowLeft size={14} /> Back to floor checklists</button>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Checklist templates</h1>
          <p className="text-sm text-slate-500 mt-1">Standardised log sheets that generate shift routines for every department and outlet.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="template-sample-download" onClick={downloadSample} className={`${btnGhost} flex items-center gap-1.5`}><DownloadSimple size={16} /> Sample CSV</button>
          {canPush && <button data-testid="template-import-button" onClick={() => setImporting(true)} className={`${btnGhost} flex items-center gap-1.5`}><UploadSimple size={16} /> Import Excel / CSV</button>}
          {canPush && <button data-testid="template-new-button" onClick={() => setEditor({})} className={`${btnPrimary} flex items-center gap-1.5`}><Plus size={16} /> New template</button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 sm:px-6 py-3">Template</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Departments</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Outlets</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Shifts</th>
              <th className="text-left px-4 py-3">Lines</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {templates.map((t) => (
              <tr key={t.id} data-testid={`template-row-${t.id}`} className={t.active ? "" : "opacity-60"}>
                <td className="px-4 sm:px-6 py-3">
                  <p className="font-bold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{categoryLabel(config, t.category)} · v{t.version}{t.source?.type === "upload" ? ` · from ${t.source.filename}` : t.source?.type === "seed" ? " · starter" : ""}</p>
                </td>
                <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{names(t.department_ids, departments, "All departments")}</td>
                <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                  <span data-testid={`template-dist-${t.id}`}>{distributionLabel(t, hier, venues)}</span>
                  {t.local?.lines > 0 && <span className="block text-[11px] font-bold text-emerald-700 flex items-center gap-1"><MapPin size={11} weight="fill" /> {t.local.lines} local line{t.local.lines === 1 ? "" : "s"} · {t.local.venues} venue{t.local.venues === 1 ? "" : "s"}</span>}
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">{(t.shifts || []).map((s) => <span key={s} className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{shiftLabel(config?.shifts, s)}</span>)}</div>
                </td>
                <td className="px-4 py-3 font-bold text-slate-700">{t.item_count}</td>
                <td className="px-4 py-3">
                  <button data-testid={`template-active-${t.id}`} disabled={!t.can_edit} onClick={() => toggleActive(t)} className={`text-xs font-bold px-2.5 py-1 rounded-md border ${t.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{t.active ? "Active" : "Paused"}</button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {canLocal && <button data-testid={`template-local-${t.id}`} onClick={() => setLocalFor(t)} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50" title="Venue-local additions"><MapPin size={18} /></button>}
                    {t.can_edit && <button data-testid={`template-edit-${t.id}`} onClick={() => setEditor(t)} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100" title="Edit"><PencilSimple size={18} /></button>}
                    {t.can_edit && <button data-testid={`template-delete-${t.id}`} onClick={() => remove(t)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete"><Trash size={18} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {templates.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No templates yet — import a log sheet or create one.</td></tr>}
          </tbody>
        </table>
      </div>

      {editor && <TemplateEditor initial={editor.id ? editor : null} config={config} departments={departments} venues={venues} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
      {localFor && <LocalLinesEditor template={localFor} venues={venues} hier={hier} initialVenueId={venueId} onClose={() => { setLocalFor(null); load(); }} />}
      {importing && <ImportWizard config={config} departments={departments} venues={venues} onClose={() => setImporting(false)} onSaved={() => { setImporting(false); load(); }} />}
    </div>
  );
}
